import { useState } from 'react';

import { AudioReviewDrawer } from './audio-review-drawer';
import { useGetAudioSpeakerFiles } from '@/api/queries/useGetAudioSpeakers';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { cn } from '@/new/lib/utils';

interface AudioReviewSummaryProps {
  matterId: string;
  className?: string;
}

function headlineLabel(fileCount: number, untaggedFileCount: number): string {
  if (untaggedFileCount === 0) return 'All speakers assigned';
  if (fileCount === 1) return 'This recording needs speaker names';
  return `${untaggedFileCount} of ${fileCount} files need speaker names`;
}

function detailLabel(fileCount: number, untaggedFileCount: number): string {
  if (untaggedFileCount === 0) {
    return `${fileCount} audio ${fileCount === 1 ? 'file' : 'files'} ready to process.`;
  }
  return 'Fact extraction is less accurate without speaker names.';
}

export function AudioReviewSummary({ matterId, className }: AudioReviewSummaryProps) {
  const { data: speakerFiles } = useGetAudioSpeakerFiles(matterId);
  const [isReviewing, setIsReviewing] = useState(false);

  if (!speakerFiles || speakerFiles.length === 0) return null;

  const untaggedFileCount = speakerFiles.filter((file) => file.untaggedSpeakerCount > 0).length;
  const hasUntagged = untaggedFileCount > 0;

  return (
    <>
      <div
        className={cn(
          '-mx-2 flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm',
          className,
        )}
      >
        <div className="min-w-0">
          {/* The status is the headline: at a glance the card should read as done
              or not, without having to parse the detail line underneath. */}
          <p className={cn('flex items-center gap-1.5 font-medium', hasUntagged ? 'text-gray-800' : 'text-green-700')}>
            {hasUntagged ? (
              <Icon className="lucide--alert-circle size-3.5 shrink-0 text-amber-600" />
            ) : (
              <Icon className="lucide--check-circle-2 size-3.5 shrink-0 text-green-600" />
            )}
            {headlineLabel(speakerFiles.length, untaggedFileCount)}
          </p>
          <p className="mt-1 text-xs text-gray-500">{detailLabel(speakerFiles.length, untaggedFileCount)}</p>
        </div>

        <Button size="sm" variant="white" className="shrink-0" onClick={() => setIsReviewing(true)}>
          Review Audio
          <Icon className="lucide--arrow-right" />
        </Button>
      </div>

      <AudioReviewDrawer
        matterId={matterId}
        files={speakerFiles}
        isOpen={isReviewing}
        onClose={() => setIsReviewing(false)}
      />
    </>
  );
}
